---
title: "Chart Patterns: Triangles, Flags, Head & Shoulders, Double Tops"
slug: chart-patterns
shortTitle: "Chart Patterns"
description: "How to recognize triangles, flags, head and shoulders and double tops, what confirms them, how measured-move targets work, and how to trade them with pending orders."
level: intermediate
category: trading
course: intermediate
order: 7
topics: [chart-patterns, technical-analysis]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/chart-patterns.svg
faqs:
  - q: "What is the difference between a chart pattern and a candlestick pattern?"
    a: "A candlestick pattern is one to three candles. A chart pattern is a larger shape built from many candles and several swings, such as a triangle or a head and shoulders, and usually takes days or weeks to form on a daily chart."
  - q: "How accurate are measured-move targets?"
    a: "They are rough guides, not destinations. Price often stops short of the target, at a nearby support or resistance level, and sometimes goes further. Many traders take profit at the nearest obvious level rather than waiting for the full projection."
  - q: "What is a false breakout?"
    a: "A false breakout is when price moves beyond a pattern's boundary and then quickly returns inside it. Waiting for a candle to close beyond the level, or for a retest, filters some of them out, but not all."
  - q: "Can I draw trendlines on the trade chart?"
    a: "The trade chart does not currently have drawing tools. You can mark patterns in a separate charting tool, note the key prices, and then place pending orders and alerts at those prices on the platform."
  - q: "Do pending orders tie up margin before they fill?"
    a: "Yes. On this platform, a pending order reserves margin as soon as you place it, so it reduces your free margin even before price reaches it. Cancel orders you no longer want from the Pending Orders tab."
---

Step back from individual candles and price often traces recognizable shapes: a series of rising lows pressing against a flat ceiling, a sharp rally followed by a tidy pause, three peaks with the middle one highest. These **chart patterns** reflect the same tug-of-war between buyers and sellers as single candles, but over dozens of candles and several swings.

This lesson covers the patterns traders watch most: triangles, flags and pennants, head and shoulders, and double tops and bottoms. You will see how each forms, what confirms it, how the classic **measured-move** target is calculated, and how to turn a pattern into orders on {{BRAND}}. Patterns describe probabilities, not certainties, and many fail.

<CTA />

<KeyTakeaways>

- **Two broad families.** Continuation patterns, such as flags and most triangles, are pauses within a trend; reversal patterns, such as head and shoulders and double tops, appear where a trend may end.
- **Confirmation is the break.** A pattern is only complete when price closes beyond its boundary, such as a neckline or a triangle edge.
- **Measured moves are rough guides.** The pattern's height projected from the breakout gives a target zone, not a promise.
- **Plan for failure.** False breakouts are common, so every pattern trade needs a stop on the other side and a size that keeps the loss affordable.

</KeyTakeaways>

## Continuation and reversal patterns

**Continuation patterns** form when a trending market pauses to consolidate before, often, resuming in the same direction. **Reversal patterns** form at the end of a move and show the trend losing control. Neither label is a rule: a continuation pattern can break the "wrong" way, and when it does, that is information too.

Patterns are built from swing highs, swing lows and trendlines, so the ideas in [Trendlines, Channels and Market Structure](/academy/article/trendlines-channels-market-structure) are the foundation for everything below.

## Triangles

A triangle forms when price swings in a narrowing range.

- **Ascending triangle**: a flat ceiling with rising lows. Buyers keep stepping in higher while sellers defend one level.
- **Descending triangle**: a flat floor with falling highs, the mirror image.
- **Symmetrical triangle**: falling highs and rising lows converging, showing contracting volatility with no clear side in control.

<Example title="An ascending triangle on BTC/USDT (illustrative prices)">

Price has stalled three times at 62,000, while the lows have risen from 59,000 to 60,200 and 61,000.

- Height at the widest point: 62,000 − 59,000 = 3,000
- Breakout: a candle closes above 62,000
- Measured-move target: 62,000 + 3,000 = **65,000**

</Example>

## Flags and pennants

A **flag** starts with a sharp, almost vertical move called the **pole**, followed by a small channel sloping gently against it. A **pennant** is the same idea with a tiny symmetrical triangle instead of a channel. Both show a market catching its breath after a strong move. The measured move adds the pole's length to the breakout point.

<Example title="A bull flag on ETH/USDT (illustrative prices)">

ETH rises from 2,900 to 3,200 in a few candles, then drifts lower in a narrow channel to 3,120. Price then closes above the channel's upper edge at 3,150.

- Pole: 3,200 − 2,900 = 300
- Measured-move target: 3,150 + 300 = **3,450**
- Stop: below the flag's low, for example 3,110

</Example>

Flags that last too long, or that retrace most of the pole, lose their meaning; the original momentum has faded.

## Head and shoulders

A **head and shoulders** is a reversal pattern at the top of an uptrend: a peak (left shoulder), a higher peak (head), then a lower peak (right shoulder). The lows between them form the **neckline**. The pattern is confirmed when price closes below the neckline. The **inverse head and shoulders** is the same shape upside down at the bottom of a downtrend.

<Example title="Head and shoulders on GOLD/USDT (illustrative prices)">

Gold peaks at 2,450, then 2,480, then 2,445. The lows between the peaks are both near 2,400, forming the neckline.

- Height: 2,480 − 2,400 = 80
- Confirmation: a close below 2,400
- Measured-move target: 2,400 − 80 = **2,320**

</Example>

## Double tops and double bottoms

A **double top** is two peaks at roughly the same price with a trough between them. It is confirmed when price closes below that trough. A **double bottom** is the mirror image and is confirmed by a close above the peak between the two lows.

<Example title="A double bottom on BTC/USDT (illustrative prices)">

Bitcoin falls to 58,000, rebounds to 61,000, falls back to 58,100 and rises again.

- Height: 61,000 − 58,000 = 3,000
- Confirmation: a close above 61,000
- Measured-move target: 61,000 + 3,000 = **64,000**

</Example>

<CompareTable caption="The main chart patterns at a glance">

| Pattern | Usual type | Confirmed by | Measured move |
| --- | --- | --- | --- |
| Ascending / descending triangle | Continuation | Close beyond the flat edge | Widest height from breakout |
| Symmetrical triangle | Either way | Close beyond either edge | Widest height from breakout |
| Flag / pennant | Continuation | Close beyond the flag | Pole length from breakout |
| Head and shoulders | Reversal | Close beyond the neckline | Head to neckline, from neckline |
| Double top / bottom | Reversal | Close beyond the middle swing | Pattern height from the break |

</CompareTable>

## Confirmation, false breakouts and retests

Most pattern traders wait for a **candle close** beyond the boundary rather than a brief poke through it. Extra evidence includes rising volume on the breakout, and a **retest**, where price comes back to the broken level and holds it from the other side.

Even so, **false breakouts** are common: price clears the level, triggers breakout orders, then reverses back inside the pattern. [Breakout vs Mean Reversion Strategies](/academy/article/breakout-vs-mean-reversion) explains why some traders specifically trade those failures.

<Warning>

A breakout entry is usually a stop order, and a stop order fills at the next available price. In a fast move, that can be well beyond the level you chose, and a failed breakout can then hit your stop loss quickly. With leverage, a few false breakouts in a row can do real damage. Size every pattern trade from the distance to your stop, not from how convincing the pattern looks.

</Warning>

## Trading a pattern on {{BRAND}}

The trade chart does not currently have drawing tools. You can mark trendlines and pattern boundaries in a separate charting tool, or simply note the key prices, then use the platform's orders and alerts at those prices.

{/* TODO: verify with platform — no drawing tools on the trade chart; neutral wording about using a separate charting tool. */}

<Steps>
<Step title="Note the levels">

Write down the breakout level, the price that would prove the pattern wrong, and the measured-move target.

</Step>
<Step title="Set a price alert">

Use the bell on the market's row, or **Account → Alerts**, to be notified when price reaches the breakout level, so you do not have to watch the chart.

</Step>
<Step title="Place a pending order">

On the **Limit / Stop** tab, a buy price above the Ask becomes a **Buy Stop** and a sell price below the Bid becomes a **Sell Stop**. Turn on **TP / SL** and enter your stop and target before you confirm.

</Step>
<Step title="Manage or cancel">

The order appears under **Pending Orders**, where you can cancel it if the pattern breaks the other way. On desktop you can also drag a pending order's line on the chart to a new price.

</Step>
</Steps>

<ProTip>

Pending orders reserve margin as soon as you place them. If you set up several pattern trades at once, check **Free margin after** in the order summary and remember that, if several fill together, they all draw on the same account. [Order Types Explained](/academy/article/order-types-explained) covers how each order type fills.

</ProTip>

## Conclusion

Chart patterns are larger versions of the story candles tell: triangles and flags show a trend pausing, head and shoulders and double tops show one possibly ending. Each has a clear confirmation, usually a close beyond a boundary, and a measured-move target that works as a rough guide. False breakouts are a normal part of trading them, so decide where you are wrong before you enter, size from that distance, and use pending orders and alerts to act on your plan rather than on impulse.
